import { Cart } from "./modules/cart.js";
import { Header } from "./modules/header.js";
import { StateManager } from "./utils/stateManager.js";

// Utility: track when elements enter the viewport for the FIRST time only
class InViewTracker {
	constructor(options = {}) {
		const defaults = {
			selector: '[data-inview-once]', // add this attr to any element you want to track once
			root: null,
			rootMargin: '0px',
			threshold: 0.1,
			onEnter: null, // function(el, entry) {}
		};
		this.opts = { ...defaults, ...options };
		this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
			root: this.opts.root,
			rootMargin: this.opts.rootMargin,
			threshold: this.opts.threshold,
		});
	}

	observeAll(root = document) {
		const nodes = root.querySelectorAll(this.opts.selector+',.product-shipping-info h2');
		nodes.forEach((el) => this.observer.observe(el));
	}

	handleIntersect(entries) {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				const el = entry.target;
				// Mark and stop observing so it only fires once per element
				this.observer.unobserve(el);
				el.classList.add('inview--entered');
				el.setAttribute('data-inview-entered', 'true');
				// Fire a custom event consumers can listen for
				el.dispatchEvent(new CustomEvent('inview:enter', { detail: { entry } }));
				// Optional callback
				if (typeof this.opts.onEnter === 'function') {
					this.opts.onEnter(el, entry);
				}
			}
		});
	}

	// Re-run to pick up any newly added nodes matching the selector
	refresh(root = document) {
		this.observeAll(root);
	}
}

class App {
	constructor(options) {
		this.defaults = {
			defaultK: false,
		};
		this.options = {};
		this.globalState = null;
		this.baseQueryEl = document;
		Object.assign(this.options, this.defaults, options);
		this.stateManager = new StateManager();

		this.namespace = "App";
		this.init();
	}

	init() {
		this.initHeader();
		document.addEventListener('DOMContentLoaded', () => {
			this.initInViewTracking();
		});
		window.addEventListener('load', () => {
			// this.initReadMore();
			this.initDynamicCart();
			this.initThumbnailNavigation();
		});
	}

	initHeader() {
		this.globalState = null;
		this.header = new Header(this.baseQueryEl, this.stateManager);
		this.header.init();
	}

	initInViewTracking() {
		// Create once and observe all matching nodes
		this.inView = new InViewTracker({
			// tweak threshold/rootMargin if you want earlier/later triggers
			threshold: 0.15,
			onEnter: (el) => {
				// Example: analytics hook or lazy class toggle
				// console.log('Entered once:', el);
			}
		});
		this.inView.observeAll(this.baseQueryEl);
	}

	initThumbnailOverflow() {
		// check if the container is overflowing and toggle the 'overflowing' class
		const productCardButtons = document.querySelector(
			".product-card__thumbnails"
		);
		if (!productCardButtons) {
			return;
		}
		if (productCardButtons.scrollWidth > productCardButtons.clientWidth) {
			productCardButtons.classList.add("overflowing");
		} else {
			productCardButtons.classList.remove("overflowing");
		}
	}

	initDynamicCart() {
		this.cart = new Cart(this.baseQueryEl, this.stateManager);
		this.cart.bindEvents();
	}

	initThumbnailNavigation() {
		this.initThumbnailOverflow();
		window.addEventListener("resize", () => {
			this.initThumbnailOverflow();
		});

		const thumbnailNavButtons =
			document.querySelectorAll(".thumbnail__nav");
		const productCardButtons = document.querySelector(
			".product-card__thumbnails"
		);
		if (!thumbnailNavButtons.length || !productCardButtons) {
			return;
		}
		const scrollAmount = 100; // Amount to scroll on each click
		thumbnailNavButtons.forEach((thumbnailNav) => {
			thumbnailNav.addEventListener("click", (event) => {
				if (event.target.classList.contains("thumbnail__nav--next")) {
					productCardButtons.scrollBy({
						left: scrollAmount,
						behavior: "smooth",
					});
				} else if (
					event.target.classList.contains("thumbnail__nav--prev")
				) {
					productCardButtons.scrollBy({
						left: -scrollAmount,
						behavior: "smooth",
					});
				}
			});
		});
	}
}

const app = new App();

// Shopify theme editor: re-init header when its section is reloaded/updated.
// Without this, the editor replaces the section HTML but the old Header instance
// (with its event listeners) still points at the removed DOM nodes.
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('header.page__header')) {
    app.header.unbindClickOutsideClosers();
    app.initHeader();
  }
});
